import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const PremiumAccess = () => {
  const plans = [
    {
      title: 'Basic',
      price: '1,500 DZD',
      period: '/month',
      features: [
        'Access to basic collection',
        '10 downloads per month',
        'Standard support',
        'Mobile access',
        'Basic search features'
      ],
      buttonText: 'Get Started',
      highlighted: false
    },
    {
      title: 'Professional',
      price: '3,000 DZD',
      period: '/month',
      features: [
        'Everything in Basic',
        'Unlimited downloads',
        'Priority support',
        'Advanced search features',
        'Offline reading',
        'Personal collections',
        'No advertisements'
      ],
      buttonText: 'Go Professional',
      highlighted: true
    },
    {
      title: 'Enterprise',
      price: '7,500 DZD',
      period: '/month',
      features: [
        'Everything in Professional',
        'Team management',
        'API access',
        'Custom integration',
        'Dedicated support',
        'Training sessions',
        'Analytics dashboard'
      ],
      buttonText: 'Contact Sales',
      highlighted: false
    }
  ];

  const [billingCycle, setBillingCycle] = React.useState('monthly');

  return (
    <Box sx={{ bgcolor: '#000000', minHeight: '100vh', color: 'white' }}>
      {/* Top Banner */}
      <Box sx={{ 
        bgcolor: '#00a651', 
        color: '#fff', 
        py: 1, 
        textAlign: 'center' 
      }}>
        <Typography>
          Special Offer: First Month Free Trial
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs with updated colors */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" sx={{ color: '#fff' }} />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link underline="hover" sx={{ color: '#00a651' }} href="/">
            Home
          </Link>
          <Typography color="#fff">Premium Access</Typography>
        </Breadcrumbs>

        {/* Hero Section with updated styling */}
        <Box sx={{
          textAlign: 'center',
          mb: 8,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100px',
            height: '4px',
            bgcolor: '#00a651'
          }
        }}>
          <Typography 
            variant="h2" 
            sx={{ 
              color: '#00a651',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              mb: 2
            }}
          >
            Flexible Pricing for Every Need
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#fff',
              mb: 4,
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Unlock premium features and elevate your experience
          </Typography>

          {/* Billing Toggle with updated colors */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: 2,
            mb: 4 
          }}>
            <Typography sx={{ color: billingCycle === 'monthly' ? '#00a651' : '#fff' }}>
              Monthly
            </Typography>
            <Button
              onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
              sx={{
                width: 60,
                height: 30,
                borderRadius: 15,
                bgcolor: '#333',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: '#00a651',
                  left: billingCycle === 'monthly' ? '4px' : '32px',
                  transition: 'left 0.3s',
                }
              }}
            />
            <Typography sx={{ color: billingCycle === 'yearly' ? '#00a651' : '#fff' }}>
              Yearly
              <Box component="span" sx={{ 
                ml: 1,
                color: '#000',
                bgcolor: '#00a651',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.8rem'
              }}>
                Save 20%
              </Box>
            </Typography>
          </Box>
        </Box>

        {/* Pricing Grid with updated styling */}
        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan) => (
            <Grid item xs={12} md={4} key={plan.title}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transform: plan.highlighted ? 'scale(1.05)' : 'none',
                borderRadius: 0,
                bgcolor: '#000',
                color: '#fff',
                border: plan.highlighted ? '2px solid #00a651' : '1px solid #333',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,166,81,0.2)',
                  transform: plan.highlighted ? 'scale(1.05)' : 'scale(1.02)',
                  transition: 'all 0.3s ease-in-out'
                }
              }}>
                {plan.highlighted && (
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    bgcolor: '#00a651',
                    color: '#fff',
                    px: 2,
                    py: 0.5
                  }}>
                    Most Popular
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Typography variant="h4" gutterBottom sx={{ color: '#fff' }}>
                    {plan.title}
                  </Typography>
                  <Box sx={{ my: 3 }}>
                    <Typography variant="h3" component="span" sx={{ color: '#00a651' }}>
                      {plan.price}
                    </Typography>
                    <Typography variant="subtitle1" component="span" sx={{ color: '#fff' }}>
                      {plan.period}
                    </Typography>
                  </Box>
                  <List>
                    {plan.features.map((feature) => (
                      <ListItem key={feature} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon sx={{ color: '#00a651' }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} sx={{ color: '#fff' }} />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    variant={plan.highlighted ? "contained" : "outlined"}
                    fullWidth
                    sx={{
                      mt: 2,
                      bgcolor: plan.highlighted ? '#00a651' : 'transparent',
                      borderColor: '#00a651',
                      color: plan.highlighted ? '#fff' : '#00a651',
                      '&:hover': {
                        bgcolor: plan.highlighted ? '#008c44' : 'rgba(0,166,81,0.1)',
                        borderColor: '#00a651',
                      }
                    }}
                  >
                    {plan.buttonText}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer section with updated colors */}
        <Box textAlign="center" sx={{ mt: 8, color: '#fff' }}>
          <Typography variant="body1">
            All prices are in Algerian Dinar (DZD). Prices include all applicable taxes.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#00a651' }}>
            Need help choosing? Contact our support team
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PremiumAccess; 